import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { ActiveToast, ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AuthenticationSelectors,
  GetGlobalNotificationsByFilter,
  GetGlobalNotificationsByFilterSuccess,
} from '../../authentication';
import { Notification } from './dashboard-notifier.types';
import { DashboardToastComponent } from './dashboard-toast.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule, ToastContainerDirective],
  selector: 'orion-platform-dashboard-notifier',
  standalone: true,
  styleUrls: ['./dashboard-notifier.component.scss'],
  templateUrl: './dashboard-notifier.component.html',
})
export class DashboardNotifierComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly toastr = inject(ToastrService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  private subscription: Subscription = new Subscription();
  private ngUnsubscribe = new Subject<void>();
  notifications = signal<Notification[]>([]);
  private activeToasts: Map<string, ActiveToast<DashboardToastComponent>> = new Map();
  toastContainer = viewChild(ToastContainerDirective);

  ngOnInit(): void {
    this.catchGetGlobalNotificationsByFilterSuccess();
  }

  ngAfterViewInit(): void {
    const container = this.toastContainer();
    if (container) {
      this.toastr.overlayContainer = container;
    }
    this.getGlobalNotifications();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.subscription.unsubscribe();
  }

  // Process notification details and assign type based on severity
  notificationDetail(notification: Notification): boolean {
    if (notification.severity === 0) {
      notification.type = 'info';
    } else if (notification.severity === 1) {
      notification.type = 'warning';
    } else if (notification.severity === 2) {
      notification.type = 'error';
    }
    this.showNotification(notification);
    return true;
  }

  // Show notification with the custom template
  showNotification(notificationData: Notification): void {
    const type = notificationData.type;
    const toast = this.toastr.show<DashboardToastComponent>(
      this.getFormattedMessage(notificationData),
      notificationData.title,
      {
        closeButton: false,
        disableTimeOut: type === 'error',
        extendedTimeOut: 0,
        positionClass: 'toast-bottom-right',
        tapToDismiss: false,
        timeOut: 0,
        toastClass: 'ngx-toastr-custom',
        toastComponent: DashboardToastComponent,
      },
      type,
    );

    if (notificationData.id && toast) {
      this.activeToasts.set(notificationData.id, toast);
    }

    if (notificationData.type === 'warning') {
      setTimeout((): void => {
        this.closeNotification(notificationData);
      }, 20000);
    }
    if (notificationData.type === 'info') {
      setTimeout((): void => {
        this.closeNotification(notificationData);
      }, 20000);
    }
  }

  getFormattedMessage(notification: Notification): string {
    return notification.message.replace(/\n/g, '<br>');
  }

  getGlobalNotifications(): void {
    const roles = this.store.selectSnapshot(AuthenticationSelectors.getCurrentRoles);
    const payload = {
      onlyValid: true,
      roles: roles,
    };
    this.store.dispatch(new GetGlobalNotificationsByFilter(payload));
  }

  private catchGetGlobalNotificationsByFilterSuccess(): void {
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(GetGlobalNotificationsByFilterSuccess),
          takeUntil(this.ngUnsubscribe),
        )
        .subscribe((res: GetGlobalNotificationsByFilterSuccess): void => {
          const records = res.notifications?.records || [];
          this.notifications.set(records);

          this.notifications().forEach((notification: Notification): void => {
            setTimeout((): void => {
              this.notificationDetail(notification);
            }, 500);
          });
        }),
    );
  }

  closeNotification(notification: Notification): void {
    if (notification.id) {
      const toast = this.activeToasts.get(notification.id);
      if (toast) {
        this.toastr.clear(toast.toastId);
        this.activeToasts.delete(notification.id);
      }
    }
  }
}
